import type { Question } from '@api/instant_survey/types';
import { Card, IconButton, RadioButtonIcon, RepromptIcon } from '@utilities';

import {
  getHierarchyPromptOptions,
  getQuestionTypeLabel,
  getScreenerPromptLabel,
  type HierarchyPromptKey,
} from '../../utilities';
import PromptText from './components/PromptText';

import styles from './_index.module.scss';

interface PreviewViewProps {
  hierarchyPromptKeys: HierarchyPromptKey[];
  isEditing: boolean;
  missingPromptKeys: string[];
  onPromptChange: (promptKey: string, value: string) => void;
  onResetPrompts: () => void;
  previewQuestion: Question;
  promptValues: Record<string, string>;
  touchedPromptKeys: Set<string>;
}

const PreviewView = ({
  hierarchyPromptKeys,
  isEditing,
  missingPromptKeys,
  onPromptChange,
  onResetPrompts,
  previewQuestion,
  promptValues,
  touchedPromptKeys,
}: PreviewViewProps) => {
  const hierarchyPromptOptions = hierarchyPromptKeys.reduce<
    Partial<Record<HierarchyPromptKey, string[]>>
  >((optionsByPromptKey, promptKey) => {
    optionsByPromptKey[promptKey] = getHierarchyPromptOptions(previewQuestion, promptKey).map(
      (option) => option.value
    );
    return optionsByPromptKey;
  }, {});

  return (
    <Card className={styles['preview-view-card']} padding="large">
      <div className={styles['preview-view-card-header']}>
        <span className={styles['preview-view-type']}>
          S1 · {getQuestionTypeLabel(previewQuestion.type).toUpperCase()}
        </span>
        <IconButton
          data-testid="screener-reset-prompts"
          onClick={onResetPrompts}
          tooltip="Reset"
          type="button"
        >
          <RepromptIcon />
        </IconButton>
      </div>
      <div className={styles['preview-view-question']}>
        <PromptText
          hierarchyPromptOptions={hierarchyPromptOptions}
          missingPromptKeys={missingPromptKeys}
          onPromptChange={onPromptChange}
          promptValues={promptValues}
          template={previewQuestion.text}
          touchedPromptKeys={touchedPromptKeys}
        />
      </div>
      <div className={styles['preview-view-answers']}>
        {previewQuestion.answers.map(({ id, text }) => (
          <div className={styles['preview-view-answer']} key={id}>
            <RadioButtonIcon />
            <div className={styles['preview-view-answer-text']}>
              <PromptText
                hierarchyPromptOptions={hierarchyPromptOptions}
                missingPromptKeys={missingPromptKeys}
                onPromptChange={onPromptChange}
                promptValues={promptValues}
                template={text}
                touchedPromptKeys={touchedPromptKeys}
              />
            </div>
          </div>
        ))}
      </div>
      {missingPromptKeys.length > 0 && (
        <p className={styles['preview-view-error']} data-testid="screener-missing-prompts-message">
          Fill all editable prompts before {isEditing ? 'applying changes' : 'adding this screener'}
          : {missingPromptKeys.map(getScreenerPromptLabel).join(', ')}.
        </p>
      )}
    </Card>
  );
};

export default PreviewView;
